/* THE APPROVER'S VIEW OF A QUOTE — Jay's mockup, applied to Pricing Approval (2026-09-29).

   "Apply the mockup here: blocks 2 and 3 folded into one gap line each, then total cost, price and
   margin, who carries the extra (A–D), the CEO decision, and the Sales OP final memo. One link back
   to the calculator for anyone who wants the detail."

   IT DRAWS FROM THE SNAPSHOT and recomputes with `computeSku`, the calculator's own function, using
   the rules frozen at submission. One implementation is the whole reason this screen and the one the
   quote was built on cannot disagree — and it is how the figures land on Jay's own without anyone
   transcribing them.

   THE LAYOUT IS HIS, not a table of the same numbers: Collab on a warm ground and Inline on a cool
   one, each pair closed by a gap row ATTACHED underneath so a comparison reads as one object, the
   option cards carrying their terms above and the net profit as the largest thing on them. The
   first build was a plain table and Max said so.

   THE KOREAN IS HIS TOO — "use the grey Korean text in the mockup as is". It sits BESIDE the English
   rather than behind the language toggle, because that is how he drew it: these labels are already
   bilingual, so putting them through i18n would mean inventing a Korean for a Korean he supplied and
   an English for an English. The portal's own strings still go through i18n as always.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Lock, X } from 'lucide-react';
/* Blocks 5 and 6, the money helpers and the derivation all live in cpSummary now: the calculator
   draws the same two blocks at the foot of a quote, and two copies of an arithmetic that has to
   agree is how they stop agreeing. */
import { fmtPct } from './pricingModel';
import { BACKEND_URL } from './backendConfig';
import ProjectPhotos from './ProjectPhotos';
import CpCalculation, { isCalcQuote } from './CpCalculation';
import useEscapeClose from './useEscapeClose';
import './ProjectPhotos.css';
import {
  n2, money, Line, cpFigures, CpColHead, CpTotalCost, CpPriceMargin, cpCards, CpCardRow,
  CpCbdKeyPoints, CbdPartnerOfferButton, CbdPriceApproval, CbdPriceApprovalExport,
  CP_SPLITS,
} from './cpSummary';

// The partner offer is laid out by the server; the rows come from the snapshot this panel prices.
const PRICING_API = `${BACKEND_URL}/api/pricing`;
/* A day as the portal writes one in English — 15/Oct/2026, never 2026-10-15 or 10/15. */
const padDay = (d) => {
  if (!d) return '—';
  const x = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(x.getTime())) return String(d);
  return `${String(x.getDate()).padStart(2, '0')}/${x.toLocaleString('en-GB', { month: 'short' })}/${x.getFullYear()}`;
};

/* A POPUP, not a panel above the list — Max, 2026-09-30: "nên cho dạng popup á click vào sẽ mở ra".
   Inline it pushed the table it was opened from a screen and a half down the page, so the row you
   clicked was nowhere near what appeared.

   Three things this codebase has already paid for:

   Z-INDEX. The portal header is 1000. A backdrop below it is covered by the header, and the buttons
   under that strip stop taking clicks while still looking pressable — the failure this file's own
   notes record three times. 1200 clears it, and the check confirms the close button is genuinely
   the element at its own centre rather than trusting the number.

   THE BACKDROP CLOSES ON A CLICK THAT BEGAN ON IT. A plain onClick closes when a drag that started
   inside the panel happens to end on the backdrop — a selection dragged off the edge of a figure
   would shut the quote. Both ends of the gesture have to be the backdrop.

   THE PAGE BEHIND DOES NOT SCROLL. The scroller here is `.portal-shell`, not `body`; locking body
   alone leaves the list sliding about under the overlay. Both are locked and both restored. */
const PadModal = ({ onClose, flash, wide, children }) => {
  const downOnBack = useRef(false);
  /* ESC CLOSES THE TOP LAYER, through the portal's one stack — it had its own document listener, so an
     Escape pressed to leave a photo opened from the head (2026-10-20) closed the whole quote with it. */
  useEscapeClose(onClose);
  useEffect(() => {
    const shell = document.querySelector('.portal-shell');
    const prev = [document.body.style.overflow, shell ? shell.style.overflow : null];
    document.body.style.overflow = 'hidden';
    if (shell) shell.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev[0];
      if (shell) shell.style.overflow = prev[1];
    };
  }, []);
  return (
    <div
      className="pad-back"
      role="presentation"
      onMouseDown={(e) => { downOnBack.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (downOnBack.current && e.target === e.currentTarget) onClose(); }}
    >
      <div className={`pad-modal${wide ? ' pad-modal-wide' : ''}`} role="dialog" aria-modal="true">
        {/* THE CONFIRMATION, INSIDE THE OVERLAY. `.pa-banner` lives on the page underneath it, so
            after pressing a card the approver saw six characters of a message behind the panel and
            nothing else. Sticky, because the button that caused it may be two screens down. */}
        {!!flash && <div className="pad-flash" role="status">{flash}</div>}
        {children}
      </div>
    </div>
  );
};

export default function PricingApprovalDetail({
  row, snapshot, skuDecisions, canDecide, canGate, busy, flash, onDecide, onGate, onMemo, onClose,
  canCalc, calcBy, onCalcSave, onCalcSend, onCalcReturn,
}) {
  const { t } = useTranslation();
  const [instruction, setInstruction] = useState(row?.ceo_instruction || '');
  const [memo, setMemo] = useState('');
  /* D sends the reader to the Instruction box rather than refusing; the ref is how. */
  const instrRef = useRef(null);
  const [needInstr, setNeedInstr] = useState(false);

  /* EVERY SKU, NOT THE FIRST — Max, 2026-09-30, asking what a quote with three of them looks
     like. It was `skus[0]`, so CP-000006 and CP-000083 (eight SKUs each) were being approved on
     the strength of one of them. Blocks 1-6 are per SKU and sit behind a tab; block 7 is not,
     because the decision it records is ONE per quote. */
  const skus = snapshot?.payload?.skus || [];
  const rules = snapshot?.rules || null;
  const [tab, setTab] = useState(0);
  /* WHICH SKUs THE APPROVER HAS ACTUALLY OPENED — Jay, 2026-10-04: "nếu có nhiều SKU thì làm sao
     để nổi bật lên để approver biết để check". A quote of eight opens on the first one and the
     other seven are a row of tabs that look like decoration; CP-000006 and CP-000083 were being
     approved on the strength of SKU #1 for exactly that reason.
     SEEN, NOT REVIEWED. This marks what has been looked at and nothing more — the verdict is taken
     in the decision dialog, where the reasons and the read-back are. Claiming a tab click was a
     review would be the screen answering for the person. It is per open panel and deliberately not
     stored: it is about this reading, not about the quote. */
  const [seen, setSeen] = useState(() => new Set([0]));
  /* THE CALCULATION STEP (Max, 04/Nov/2026): what Amy has typed and not saved yet, so the Price Approval sheet under the
     table follows it while she tries percentages. Null = nothing pending; the snapshot is what is shown. */
  const [calcLive, setCalcLive] = useState(null);
  useEffect(() => { setSeen((p) => (p.has(tab) ? p : new Set([...p, tab]))); }, [tab]);
  /* NO COSTING AXIS — Max, 2026-09-30: "option và card là 1". The screen asked the question
     twice, once for the costing and once for who carries the extra, and picking a costing hid the
     others so the approver could never see them side by side. Jay's own instruction was one list
     from the start: "who carries the extra (A–D)".
     Blocks 1 to 6 describe the quote AS BUILT. The cards below them are the ways it can be closed,
     and each carries its own costing. */
  /* THE SKUs STILL IN. A verdict taken at the manager step takes its SKU out of everything the
     final approver is shown a figure for — the cards especially, which fold the whole order and
     would otherwise price one nobody is approving (Max, 2026-10-04: "tính lại theo SKU còn được
     duyệt"). Matched on POSITION, which is what the verdict is keyed on, and only for the snapshot
     it was taken against — the server sends none for any other. */
  const refused = useMemo(() => new Set((skuDecisions || [])
    .filter((d) => d.decision === 'rejected').map((d) => Number(d.sku_index))), [skuDecisions]);
  const liveSkus = useMemo(() => skus.filter((_, i) => !refused.has(i)), [skus, refused]);
  const sku = skus[Math.min(tab, Math.max(skus.length - 1, 0))] || null;
  const fig = useMemo(() => cpFigures(sku, rules), [sku, rules]);
  const figs = useMemo(() => skus.map((x) => cpFigures(x, rules)).filter(Boolean), [skus, rules]);
  const c = fig?.c || null;

  if (!sku || !c) {
    /* A quote submitted before the freeze existed has no snapshot. Saying so is the honest answer:
       drawing this from the live row would show figures nobody approved. */
    return (
      <PadModal onClose={onClose} flash={flash}>
        <div className="pad-wrap">
          <div className="pad-head">
            <b>{row?.project_id} — {row?.name}</b>
            <button type="button" className="pad-close" onClick={onClose}
              title={t('pricing.padClose')} aria-label={t('pricing.padClose')}>
              <X size={18} />
            </button>
          </div>
          <p className="pad-none">{t('pricing.padNoSnapshot')}</p>
          {/* The photos are stored apart from the snapshot, so a quote with none still shows them. */}
          <ProjectPhotos scope="cpq" ownerKey={row?.project_id} readOnly compact />
        </div>
      </PadModal>
    );
  }

  /* Blocks 2, 3, 4 and 7 are this screen's own; 5 and 6 are the shared ones. Both read the SAME
     derivation, so the figure a block prints here is the figure the calculator prints there. */
  const {
    qty, benchFactory, benchFrame, hasBench, extra, partnerUnit, msrp, ohPct,
  } = fig;
  const v = sku.values || {};

  /* BLOCK 7's CARDS come out of `cpCards`, the same call the calculator makes at the foot of a
     quote — see cpSummary for why this list has the shape it has. Nothing is computed here. */
  const { cards, cols, ord } = cpCards(skus, rules, t, (c && c.items) || {}, liveSkus);

  const decided = row?.extra_option || '';
  /* What the record calls the card that was chosen. An option can be DELETED off the quote after
     it was picked, and the stored key still says which one it was, so the number is read out of
     the key rather than looked up — a decision that prints as nothing is worse than one that
     prints as "Option 2, no longer on the quote". */
  /* ONE LIST since 2026-10-04 — every way the quote can be closed is a card on the row, so the
     lookup is one search. While there were two rows, a lookup that knew only about the four
     printed "Option 1 — this option is no longer on the quote" for a costing sitting two inches
     below it; that cannot happen again by construction. */
  const cardOf = (k) => cards.find((x) => x.key === k) || null;
  const cardName = (k) => {
    const hit = cardOf(k);
    /* THE RECORD CARRIES NO POSITIONAL LETTER, 2026-10-04. Every card on the row now shows one —
       A, B, C, then D, E for the quote's own costings — but that letter is the card's PLACE, and
       the place moves: delete Option 1 and what was E becomes D, so a record reading "D · Option
       2" would be describing a different card a week later. A, B and C are the exception and are
       printed with theirs because for those three the letter IS the stored key and cannot move.
       Everything else is named, which is what is on the card anyway. */
    if (hit) return hit.name;
    /* A key the row no longer draws. 'O1'..'O99' is the one case that can happen: the quote's
       options were cards on this row for a day (2026-09-30 to 2026-10-01) and a decision taken
       then still says which one it was. Printed as what it was rather than swallowed. */
    return /^O\d+$/.test(k) ? t('pricing.padOptionN', { n: Number(k.slice(1)) }) : k;
  };
  /* A costing card carries its BASIS above its explanation — "Priced at A · partner pays the
     extra" — and that basis is half of what was agreed to, so the record prints it. The four fixed
     cards have no basis line because they ARE the basis. */
  const cardBlurb = (k) => {
    const hit = cardOf(k);
    return hit ? [hit.whoHead, hit.who].filter(Boolean).join(' \u00b7 ') : null;
  };
  const memoLocked = !!String(row?.sop_memo || '').trim();

  return (
    /* WIDER FOR LINDA'S SHEET — Max, 28/10: "tăng width lên thêm". Its 21 columns fitted 1,180 px only by squeezing; the
       guideline panel keeps its width. */
    <PadModal onClose={onClose} flash={flash} wide={!!(c && c.cbd)}>
    <div className="pad-wrap">
      {/* THE HEAD STAYS PUT — Max, 2026-10-01, marking the whole strip on a screenshot:
          "phần này cần freeze or stick lại nhé". The panel is five screens long, and the
          three things that say WHICH quote and WHICH SKU you are reading — the id, the SKU
          tabs and the Collab/Inline column names — all scrolled away on the first block,
          leaving a wall of figures belonging to nothing. The column strip was already
          sticky on its own; this sticks the four of them as ONE object, which is also why
          the inner one gives its stickiness up — two sticky boxes, one inside the other,
          separate at the top and nothing holds them in line. */}
      <div className="pad-sticky">
      {/* The quote's id, the frozen line and the SKU tabs on the left; the two buttons on the right. */}
      <div className="pad-top">
      <div className="pad-topl">
      <div className="pad-head">
        <div>
          <b data-val>{row.project_id} — {row.name}</b>
          <span className="pad-sub">
            {row.team_name || row.team_key || '—'} · {row.approval_status}
            {row.submitted_by ? ` · ${t('pricing.padSubmittedBy')} ${row.submitted_by}` : ''}
            {/* THE WHOLE ORDER ON A QUOTE WITH SEVERAL SKUs. It printed `qty`, which is the OPEN
                SKU's quantity — so a quote of three said "50 EA" beside its id while the tab strip
                underneath said 50, 120 and 200, and the one number that looked like the quote's
                was the smallest of them. */}
            {skus.length > 1
              ? ` · ${n2(ord.qty)} EA · ${skus.length} SKU`
              : ` · ${qty} EA`}
          </span>
        </div>
      </div>

      {snapshot?.live ? (
        <p className="pad-frozen pad-live">{t('pricing.padLiveDraft')}</p>
      ) : (
        <p className="pad-frozen">
          <Lock size={12} /> {t('pricing.padFrozen')}
          {row.editedSince ? <b className="pad-edited"> {t('pricing.padEdited')}</b> : null}
        </p>
      )}

      {/* The two grounds, named once at the top. Sticky, so a figure halfway down still says which
          side it is on. */}
      {/* ONE TAB PER SKU, and only when there is more than one — a single tab is a control that
          does nothing but take a line. The tab carries the SKU's own quantity, because that is
          what changes between them and what the blocks below are priced on. */}
      {/* THE QUOTE'S PHOTOS, beside the SKU tabs — Max, 2026-10-20: "thêm hình đã đính kèm vào khi mở
          phiếu pricing trong approval nhé". The photos are the QUOTE's (scope cpq, keyed by its CP id),
          not one SKU's, so they sit in the head with the quote's id and stay in view while the blocks
          scroll. A row of small thumbnails, read-only; a click opens the full viewer. No photo, no strip. */}
      {skus.length > 1 && (
        <>
        <div className="pad-tabs" role="tablist">
          {skus.map((sk, i) => {
            const out = refused.has(i);
            return (
              <button key={i} type="button" role="tab" aria-selected={i === tab}
                className={`${i === tab ? 'on' : ''}${out ? ' cut' : ''}${seen.has(i) || out ? '' : ' unseen'}`}
                onClick={() => setTab(i)}>
                <b data-val>{sk.name || t('pricing.padSkuN', { n: i + 1 })}</b>
                <em>
                  {`${n2((figs[i] || {}).qty)} EA`}
                  {/* A WORD, NOT A DOT. Three states on a tab strip — read, not read yet, already
                      refused — cannot be carried by colour and a mark: in print, in forced colours
                      and to a reader who cannot separate the two tints, a row of dots says
                      nothing. The tint only groups them. */}
                  {out ? ` · ${t('pricing.padSkuCut')}`
                    : seen.has(i) ? '' : ` · ${t('pricing.padSkuUnseen')}`}
                </em>
              </button>
            );
          })}
        </div>
        {/* HOW FAR THROUGH THE QUOTE THE READER IS. The tabs say which SKU is open; this says how
            many are left, which is the thing that was invisible — a quote of eight looked exactly
            like a quote of one until you counted the tabs. */}
        <p className={`pad-skuprog ${seen.size >= skus.length ? 'done' : ''}`}>
          {seen.size >= skus.length
            ? t('pricing.padSkuAllSeen', { n: skus.length })
            : t('pricing.padSkuSeen', { n: seen.size, all: skus.length })}
          {refused.size > 0 ? ` · ${t('pricing.padSkuCutN', { n: refused.size })}` : ''}
        </p>
        </>
      )}
      </div>
      <div className="pad-topr">
        <div className="pad-head-btns">
          {/* Jay's one link out. This screen is the decision; the calculator is where the detail is. */}
          <a className="sp-btn" href={`/sales/pricing-calculator?open=${encodeURIComponent(row.project_id)}`}>
            <ExternalLink size={13} /> {t('pricing.padOpenCalc')}
          </a>
          {/* AN X, NOT THE WORD — Max, 2026-10-01, marking it on the screenshot. It is the one
              control on this panel that is not about the quote, and every other overlay in the
              portal closes with the same mark; the word sat in the row beside "Open in the
              calculator" reading as a second thing you could do to the quote.
              `title` AND `aria-label`, because an icon with neither is a button that says nothing
              to a screen reader or to a hover. */}
          <button type="button" className="pad-close" onClick={onClose}
            title={t('pricing.padClose')} aria-label={t('pricing.padClose')}>
            <X size={18} />
          </button>
        </div>
      </div>
      </div>

      {/* THE QUOTE'S PHOTOS UNDER THE SKU TABS, left, above the Collab / Inline strip — Max, 2026-10-20,
          pointing there: "chuyển xuống đây nhé" (they sat under the head buttons for an hour). 96px, up to
          eight; the eighth says "+N" for the rest. */}
      <div className="pad-photorow">
        <ProjectPhotos scope="cpq" ownerKey={row.project_id} readOnly compact max={8} />
      </div>

      {/* A CBD quote reads as Linda's sheet and nothing else (Linda, 28/10: "nó dài hơn sheet của c làm"; Max: "cắt theo
          hiển thị đúng mấy thông tin như sheet của chị") — so no Collab / Inline strip, whose blocks are not drawn for it */}
      {!c.cbd && <CpColHead src={v.bench_src} />}
      </div>

      {/* ---- block 1, the order ---------------------------------------------------------
          Jay's fold list for this screen starts at block 2, and the order was left with it — but
          the terms he folded are the two COSTS, not the quantity and the dates those costs were
          quoted against. An approver deciding $3,109 on 50 EA at an 8-day lead is weighing exactly
          those. Read-only, one row, no card of its own. */}
      {/* ---- the CBD method and the offer's own facts -------------------------------------
          Max, 2026-10-26, on the panel of a CBD quote: the approver could not see which way it
          was priced, nor whom the offer is for, at which term and for how long — all of which the
          workbook's CEO Approval sheet prints in its head. Read-only, the calculator's own values. */}
      {/* THE PRICE APPROVAL SHEET FIRST — Linda's template, approved by Tina (Max, 2026-10-26: "làm nhé"): the options
          against the inline product, the margin sensitivity, and the option YL ticks — the last option is his own Gross
          Margin %. The tick IS the decision on the card (extra_option: 'A', 'B', 'O1'.., 'GM30'); the blocks below stay
          what was submitted, as the detail. */}
      {/* THE SHEET'S HEAD — the template's B5:J10, the same facts in the same two columns */}
      {c.cbd && (
        <div className="pad-block cbd-pa-head" data-cbd-head>
          <div className="cbd-pa-headgrid">
            {[[t('pricing.paHProject'), row?.name || '—'],
              [t('pricing.paHPriceTerm'), v.priceTerm ?? 'FOB HCM, Vietnam'],
              [t('pricing.paHPartner'), v.partnerName || '—'],
              [t('pricing.paHCurrency'), 'USD'],
              [t('pricing.paHSeason'), v.offerSeason || '—'],
              [t('pricing.poDate'), padDay(v.poDate)],
              [t('pricing.paHFactory'), v.factoryName || '—'],
              [t('pricing.xfDate'), padDay(v.xfDate)],
              [t('pricing.paHPrepared'), row?.submitted_by || row?.created_by || '—'],
              [t('pricing.paHLead'), c.lead === null || c.lead === undefined ? '—' : `${c.lead}`],
              [t('pricing.paHReviewed'), row?.manager_by || '—'],
              [t('pricing.paHApprover'), row?.extra_option_by || '—']].map(([k, val]) => (
                <div key={k}><span>{k}</span><b data-val>{val}</b></div>
            ))}
          </div>
        </div>
      )}
      {c.cbd && (row?.approval_status === 'Pending calculation' || isCalcQuote(skus)) && (
        <CpCalculation skus={skus} rules={rules} editable={!!canCalc} busy={busy} calcBy={row?.calc_by || calcBy}
          onLive={setCalcLive} onSave={onCalcSave} onSend={onCalcSend} onReturn={onCalcReturn} />
      )}
      {c.cbd && (
        <CbdPriceApproval skus={calcLive || (liveSkus.length ? liveSkus : skus)} rules={rules} decided={decided}
          canDecide={!!canDecide} busy={busy} onPick={(key) => onDecide(key, instruction)}
          record={(
            <>
              <div><span>{t('pricing.paApprovedBy')}</span><b>{row.extra_option_by || '—'}</b></div>
              <div><span>{t('pricing.paApprovalDate')}</span><b>{row.extra_option_at ? String(row.extra_option_at).slice(0, 10) : '—'}</b></div>
              <div className="cbd-pa-comments">
                <span>{t('pricing.paYlComments')}</span>
                {canDecide ? (
                  <textarea ref={instrRef} rows={2} value={instruction} placeholder={t('pricing.paYlCommentsPh')}
                    onChange={(e) => setInstruction(e.target.value)} data-cbd-comments />
                ) : <b className={instruction ? '' : 'blank'}>{instruction || '—'}</b>}
                {canDecide && !!decided && (
                  <button type="button" className="sp-btn pad-savins" disabled={busy} onClick={() => onDecide(decided, instruction)}>
                    {t('pricing.padSaveInstruction')}
                  </button>
                )}
              </div>
            </>
          )}
          foot={(
            <div className="pad-offerbar">
              <CbdPriceApprovalExport projectId={row?.project_id} skus={liveSkus.length ? liveSkus : skus} rules={rules}
                decided={decided} apiBase={PRICING_API}
                meta={{ project: row?.name || '', preparedBy: row?.submitted_by || row?.created_by || '',
                  reviewedBy: row?.manager_by || '', approver: '' }} />
              <CbdPartnerOfferButton projectId={row?.project_id} skus={liveSkus.length ? liveSkus : skus} rules={rules}
                decided={decided} approved={row?.approval_status === 'Approved'} apiBase={PRICING_API} />
            </div>
          )} />
      )}
      {!c.cbd && (<>
      <div className="pad-block">
        <div className="pad-btitle">
          <h4>{t('pricing.padB1')}</h4><span className="pad-kind">{t('pricing.padKindOrder')}</span>
        </div>
        <div className={c.cbd ? 'pad-order pad-order7' : 'pad-order'}>
          {[[t('pricing.skuName'), sku.name || '—'],
            ...(c.cbd ? [[t('pricing.cbdColor'), v.color || '—']] : []),
            [t('pricing.qty'), `${qty} EA`],
            [t('pricing.poDate'), padDay(v.poDate)],
            [t('pricing.xfDate'), padDay(v.xfDate)],
            /* `slRate` is a FRACTION, not a percentage — 0.5, and printed raw it said the quote
               carried a 0.5% short-lead surcharge where the real figure is 50%. `fmtPct` is the
               model's own formatter and is what the calculator prints it with. */
            [t('pricing.leadLabel'), c.lead === null
              ? t('pricing.leadNone')
              /* On the CBD method the short-lead answer has its own cell beside this one. */
              : (c.cbd ? `${c.lead} ${t('pricing.days')}` : t('pricing.padLeadDays', { d: c.lead, r: fmtPct(c.slRate) }))],
            ...(c.cbd ? [[t('pricing.cbdSlFlag'), v.slFlag === 'yes' ? t('pricing.cbdSlYes')
              : (v.slFlag === 'no' ? t('pricing.cbdSlNo') : t('pricing.cbdSlAuto', { rate: fmtPct(c.slRate) }))]] : [])].map(([k, val]) => (
              <div key={k}><span>{k}</span><b data-val>{val}</b></div>
          ))}
        </div>
      </div>

      {/* ---- blocks 2 and 3, folded to one gap line each --------------------------------- */}
      {/* ---- blocks 2 and 3, PAIRED AND OPEN --------------------------------------------
          Max, 2026-09-30: "cho hiện ra luôn chứ ko cần gấp lại nhưng mà phải đủ các thông tin á;
          hiện tôi thấy bạn đang dùng con số tổng để so sánh."

          This reverses Jay's 29/9 fold for this screen, deliberately and on Max's instruction —
          worth telling Jay rather than leaving him to notice. His reasoning was sound for a
          decision screen, and Max's answers it: a single total says the two sides differ by $7.66
          and nothing about WHERE, so an approver cannot tell a vendor's CMT from a packaging spec
          without leaving the screen. The lines are what is being approved.

          Read-only, and built as the same pair the other blocks use, so blocks 2, 3, 5 and 6 are
          one object repeated rather than four layouts. */}
      {[{ n: 2, k: 'padB2', c: n2(c.factoryCost), i: benchFactory,
          lines: [{ k: 'skin', tk: 'skin' }, { k: 'cmt', tk: 'cmt' }, { k: 'ohp', tk: 'ohp' }],
          src: v.bench_src, more: true },
        { n: 3, k: 'padB3', c: n2(c.frameCost), i: benchFrame,
          lines: [{ k: 'frame', tk: 'frame' }],
          /* THE MODEL LEADS, ON BOTH SIDES — Max, 2026-09-30. It is the answer to "what are these
             two prices for", so under the price it read as a footnote to a figure the reader had
             already tried to judge. The inline side carries the SAME model and says so: the
             benchmark follows block 3's pick by design ("One place picks the frame model; the
             inline side follows it"), so the gap is the price and not the part.
             `data-val` marks a cell printing a STORED value rather than a portal label — a model
             name is the vendor's, not ours, and is the same in either language. */
          headC: [[t('pricing.padFrameModel'), <span data-val>{v.frameModel || '—'}</span>]],
          headI: [[t('pricing.padFrameModel'),
            <span><span data-val>{v.frameModel || '—'}</span> <small>{t('pricing.benchFromCollab')}</small></span>]],
          /* Left under the price because they are not what the two sides are compared on: how the
             frame reaches the vendor is what decides the frame profit and the FM fee in block 4. */
          notes: [[t('pricing.frameSupply'), t((v.supply || 'sell') === 'sell' ? 'pricing.supplySell' : 'pricing.supplyFoc')],
            [t('pricing.fmfee'), money(n2(c.fmfee))]] }].map((b) => {
        const d = b.c - b.i;
        const rows = b.lines.map((l) => {
          const theirs = String(v[`bench_${l.k}`] ?? '').trim();
          return {
            k: l.k,
            label: t(`pricing.${l.tk}`),
            mine: l.k === 'frame' ? n2(c.frameCost) : n2(v[l.k]),
            theirs: theirs === '' ? null : Number(theirs),
            note: String((v.benchWhy || {})[l.k] ?? '').trim(),
          };
        });
        const more = b.more ? (v.benchWhyMore || []).filter((r) => r && (r.title || r.amt || r.note)) : [];
        return (
          <div className="pad-block" key={b.n}>
            <div className="pad-btitle">
              <h4>{t(`pricing.${b.k}`)}</h4>
              <span className="pad-kind">{t('pricing.padKindCompare')}</span>
            </div>
            <div className="pad-pair">
              <div className="pad-card c">
                <p className="pad-cap">{t('pricing.padCollab')}<span className="pad-tot">{money(b.c)}</span></p>
                {(b.headC || []).map(([k, val]) => <Line key={k} l={k} r={val} />)}
                {rows.map((r) => <Line key={r.k} l={r.label} r={money(r.mine)} />)}
                {(b.notes || []).map(([k, val]) => <Line key={k} dim l={k} r={val} />)}
              </div>
              <div className="pad-card i">
                <p className="pad-cap">{t('pricing.padInline')}<span className="pad-tot">{hasBench ? money(b.i) : '—'}</span></p>
                {(b.headI || []).map(([k, val]) => <Line key={k} l={k} r={val} />)}
                {rows.map((r) => (
                  <Line key={r.k} l={r.label}
                    r={r.theirs === null ? '—' : money(r.theirs)} />
                ))}
                {b.src ? <p className="pad-srcline" title={b.src}>{t('pricing.benchSrc')} {b.src}</p> : null}
              </div>
              <div className="pad-gap">
                <div className="pad-eqrow">
                  <span>{t('pricing.padCollab')} <b>{money(b.c)}</b> − {t('pricing.padInlineShort')} <b>{hasBench ? money(b.i) : '—'}</b></span>
                  <span className="pad-lead">
                    {hasBench ? (
                      <>
                        {t('pricing.gapWord')}{' '}
                        <b className={d > 0.005 ? 'pad-up' : d < -0.005 ? 'pad-down' : 'pad-same'}>
                          {d > 0.005 ? '▲ +' : d < -0.005 ? '▼ −' : ''}
                          {Math.abs(d) < 0.005 ? t('pricing.padSameWord') : money(Math.abs(d))}
                          {b.i > 0 && Math.abs(b.c / b.i - 1) >= 0.005
                            && <small>{` (${(b.c / b.i).toFixed(2)}×)`}</small>}
                        </b>
                      </>
                    ) : t('pricing.padNoBench')}
                  </span>
                </div>
                {/* EVERY LINE, not only the ones that differ. On the calculator the table hides
                    the matching lines because the person is typing and a quiet row is noise; here
                    it is the record of what was compared, and a line left out reads as a line
                    nobody looked at. */}
                <div className="pad-whys">
                  <div className="pad-whyhead">
                    <span>{t('pricing.benchLineCol')}</span>
                    <span className="d">{t('pricing.benchDiff')}</span>
                    <span>{t('pricing.benchWhyCol')}</span>
                  </div>
                  {rows.map((r) => {
                    const ld = r.theirs === null ? null : r.mine - r.theirs;
                    return (
                      <div className="pad-why" key={r.k}>
                        <span>{r.label}</span>
                        <span className={`d ${ld > 0.005 ? 'pad-up' : ld < -0.005 ? 'pad-down' : ''}`}>
                          {ld === null ? '—'
                            : Math.abs(ld) < 0.005 ? t('pricing.padSameWord')
                              : `${ld > 0 ? '▲ +' : '▼ −'}${money(Math.abs(ld))}`}
                        </span>
                        <span className={r.note ? '' : 'blank'}>{r.note || t('pricing.padNoReason')}</span>
                      </div>
                    );
                  })}
                  {more.map((r, i) => (
                    <div className="pad-why" key={`m${i}`}>
                      <span>{r.title || '—'}</span>
                      <span className="d">{r.amt === '' || r.amt === null || r.amt === undefined ? '—' : money(r.amt)}</span>
                      <span className={r.note ? '' : 'blank'}>{r.note || t('pricing.padNoReason')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* ---- block 4, collab only -------------------------------------------------------- */}
      <div className="pad-block">
        <div className="pad-btitle">
          <h4>{t('pricing.padB4')}</h4>
          <span className="pad-kind">{t('pricing.padKindSolo')}</span>
        </div>
        <div className="pad-card c pad-full">
          <p className="pad-cap">{t('pricing.padExtraPerUnit')}<span className="pad-tot">{money(extra)}</span></p>
          {/* `moq` is the only item whose model key is not its own i18n key — the label is
              `moqCharge` — so a bare `pricing.${k}` printed the raw word "moq" on the screen. The
              map is written out rather than guessed at. */}
          {/* EVERY item, zeros included — Max, 2026-09-30: "phải đủ các thông tin". A line left
              out reads as a line nobody looked at; a line at — says it was considered and is nil,
              which is the difference between a ledger and a summary. */}
          {Object.entries(c.items || {}).map(([k, x]) => (
            <Line key={k} dim={n2(x) === 0}
              l={t(`pricing.${k === 'moq' ? 'moqCharge' : k}`)} r={n2(x) === 0 ? '—' : money(x)} />
          ))}
          {/* WHICH LINE IS THE EXTRA, said once. Jay's mockup carries this sentence and it is the
              one thing in the block that cannot be read off the figures at a glance: at this
              quantity the MOQ surcharge alone is most of the extra, which is the fact an approver
              acts on — a bigger order moves it, arguing about handling does not. Only drawn when
              that line really dominates, or it becomes a sentence saying "5% of the extra". */}
          {n2(c.items?.moq) > 0 && extra > 0 && (n2(c.items.moq) / extra) >= 0.5 && (
            <p className="pad-note pad-moqnote">
              {t('pricing.padMoqNote', {
                amt: money(c.items.moq),
                pct: Math.round((n2(c.items.moq) / extra) * 100),
              })}
            </p>
          )}
        </div>
      </div>

      {/* ---- blocks 5 and 6, the two the calculator also draws ------------------------- */}
      <CpTotalCost fig={fig} />
      <CpPriceMargin fig={fig} />
      </>)}

      {/* ---- block 7, the cards -------------------------------------------------------- */}
      {/* ONE FLAT LIST — Max, 2026-09-30: "tui đã nói là option và card là 1. Nếu user tạo phiếu
          thì có sẵn mặc định 3 card, nếu cần thì user tạo thêm card/option khác thôi."
          It was two questions on two axes — pick a costing at the top of the panel, then pick a
          split down here — so the three cards on screen were three splits of ONE costing and the
          others were hidden behind a chooser two screens up. Nothing said so, which is why the
          same numbers kept reading as wrong. Jay's instruction was one list from the start:
          "who carries the extra (A–D)". Every way this quote can be closed is now a card in that
          list, all of them visible at once, and the decision is one click on one of them. */}
      {!(c && c.cbd) && (
      <div className="pad-block">
        <div className="pad-btitle">
          <h4>{t(c && c.cbd ? 'pricing.cbdB7' : 'pricing.padB7', { amt: money(ord.extra), n: ord.qty })}</h4>
          <span className="pad-kind">{t('pricing.padB7Kind')}</span>
        </div>
        <p className="pad-note">
          {c && c.cbd ? t('pricing.cbdB7Note') : skus.length > 1
            ? t('pricing.padB7NoteMany', { n: liveSkus.length, e: money(ord.extra) })
            : t('pricing.padB7Note', { m: money(msrp), p: money(partnerUnit), e: money(extra) })}
        </p>
        {/* SAID WHERE THE FIGURES ARE, not only on the tab strip. Every number on this row folds
            the order, so once a SKU has been refused the row is about a smaller order than the
            quote — and an approver reading $3,697 has no way of knowing which order it is. */}
        {refused.size > 0 && (
          <p className="pad-cardscope">
            {t('pricing.padCardScope', { n: liveSkus.length, all: skus.length })}
          </p>
        )}
        {/* The row is the SHARED one; only the foot differs — a Select button here, nothing on
            the calculator, because this is the screen that can decide. */}
        <CpCardRow cards={cards} cols={cols} decided={decided} ohPct={ohPct} rowClass="pad-opts-all"
          foot={(o) => (canDecide ? (o.hand ? (
            <>
              {/* THE TERMS CARD POINTS AT THE INSTRUCTION BOX, which is Jay's own design for it —
                  "a D that points to the CEO instruction box". The box is BELOW the cards, so the
                  one card with a prerequisite is the one that looks broken until it says so.
                  Pressed empty it takes the reader to the box and puts the cursor in it, which is
                  the next thing to do, rather than refusing: a button that does nothing and
                  explains nothing is the worst of the three states. */}
              <button type="button" className="pad-pick" disabled={busy}
                onClick={() => {
                  if (String(instruction).trim().length < 4) {
                    const el = instrRef.current;
                    if (el) { el.scrollIntoView({ block: 'center' }); el.focus(); }
                    setNeedInstr(true);
                    return;
                  }
                  onDecide(o.key, instruction);
                }}>
                {t('pricing.padSelect', { key: o.letter })}
              </button>
              {needInstr && String(instruction).trim().length < 4 && (
                <p className="pad-dneed">{t('pricing.padNeedInstruction')}</p>
              )}
            </>
          ) : (
            <button type="button" className="pad-pick" disabled={busy}
              onClick={() => onDecide(o.key, instruction)}>
              {t('pricing.padSelect', { key: o.letter })}
            </button>
          )) : null)} />
        {c && c.cbd && <CpCbdKeyPoints skus={liveSkus.length ? liveSkus : skus} rules={rules} decided={decided} />}
        {c && c.cbd && (
          <div className="pad-offerbar">
            <CbdPartnerOfferButton projectId={row?.project_id} skus={liveSkus.length ? liveSkus : skus} rules={rules}
              decided={decided} approved={row?.approval_status === 'Approved'} apiBase={PRICING_API} />
          </div>
        )}
      </div>
      )}

      {/* ---- the settings these figures were worked out with ---------------------------- */}
      {/* FROM JAY'S MOCKUP, the strip at its foot — Max, 2026-10-01: "tôi thấy trong của Jay có
          thêm cái này nữa". Five numbers the whole panel is priced on and that are stated nowhere
          else on it: an approver reading a 31% margin has no way of knowing it was worked out at a
          15% O.H on base cost until this says so.
          READ-ONLY, and that is not a quiet reinterpretation of his "change only if needed": his
          own point 4 is that this screen FREEZES its figures at submission, and a control here
          that re-prices the quote would be the approver deciding on numbers nobody submitted. What
          the approver changes is the decision, and D plus the Instruction box is where anything
          outside these settings is said.
          FOLDED BY DEFAULT. It is the answer to "where did that come from", which is a question
          asked occasionally and never on the way in. */}
      {!(c && c.cbd) && (<>
      <details className="pad-settings">
        <summary>{t('pricing.padSettings')}</summary>
        <div className="pad-settings-row">
          {(c && c.cbd ? [
            [t('pricing.method'), t('pricing.methodCbd')],
            [t('pricing.padSetOh'), t('pricing.padSetOhVal', { pct: n2(c.cbd.ohPct) })],
            [t('pricing.cbdSetMarkup'), `× ${(1 + n2(c.cbd.markupPct) / 100).toFixed(2)}`],
            [t('pricing.padSetPremium'), v.premium === '' || v.premium === undefined ? '—' : `${n2(v.premium)}%`],
          ] : [[t('pricing.padSetPremium'), v.premium === '' || v.premium === undefined
            ? '—' : `${n2(v.premium)}%`],
          [t('pricing.padSetPartner'), fig.partnerPct ? `${n2(fig.partnerPct)}%` : '—'],
          /* C's own ratio, read from the split table rather than typed here as "50 / 50": two
             places saying what one card does is how they come to disagree. */
          [t('pricing.padSetSplitC'), `${CP_SPLITS.C} / ${100 - CP_SPLITS.C}`],
          [t('pricing.padSetOh'), ohPct ? t('pricing.padSetOhVal', { pct: n2(ohPct) }) : '—'],
          [t('pricing.padSetCase'), v.dealCase
            ? t(`pricing.dealCase${v.dealCase === 'both' ? 'Both' : v.dealCase.toUpperCase()}`)
            : '—']]).map(([k, val]) => (
              <div key={k}><span>{k}</span><b data-val>{val}</b></div>
          ))}
        </div>
      </details>

      {/* ---- the decision on the record --------------------------------------------------- */}
      <div className="pad-decision">
        <div className="pad-dhead">
          <h5>{t('pricing.padCeoDecision')}</h5>
          <span>{t('pricing.padCeoDecisionSub')}</span>
        </div>
        {/* THE COSTING ROW IS GONE with the axis it recorded — one card IS one costing now, so
            the row below says everything the two said together. `chosen_option` and
            `submitted_option` stay on the table, empty: they were added and retired on the same
            day and no quote ever carried one, so there is nothing to migrate and nothing to lose
            by leaving two unread columns where they are. */}
        <div className="pad-drow">
          <label>{t('pricing.padChosen')}</label>
          <div className={`v ${decided ? '' : 'blank'}`}>
            {decided ? cardName(decided) : t('pricing.padUndecided')}
            {/* The chosen card's own words, so the record reads as a decision rather than as a
                letter. An option deleted off the quote since has none — and says so. */}
            {!!decided && (cardOf(decided)
              ? <small className="pad-cardwho">{cardBlurb(decided)}</small>
              : <small className="pad-moved">{t('pricing.padCardGone')}</small>)}
          </div>
        </div>
        <div className="pad-drow">
          <label>{t('pricing.padInstruction')}</label>
          {canDecide ? (
            <textarea ref={instrRef} rows={2} value={instruction}
              className={needInstr && String(instruction).trim().length < 4 ? 'need' : ''}
              placeholder={t('pricing.padInstructionPh')}
              onChange={(e) => { setInstruction(e.target.value); if (needInstr) setNeedInstr(false); }} />
          ) : (
            <div className={`v ta ${instruction ? '' : 'blank'}`}>{instruction || t('pricing.padInstructionPh')}</div>
          )}
        </div>
        <div className="pad-drow">
          <label>{t('pricing.padDrowFinal')}</label>
          {/* The Sales OP memo IS the final terms — one field, not two people writing the same thing
              in two boxes. It shows here once written and is filled in below until then. */}
          <div className={`v ${memoLocked ? '' : 'blank'}`}>
            {memoLocked ? row.sop_memo : t('pricing.padMemoWaitShort')}
          </div>
        </div>
        <div className="pad-drow">
          <label>{t('pricing.padDrowRecorded')}</label>
          <div className={`v ${row.extra_option_by ? '' : 'blank'}`}>
            {row.extra_option_by
              ? `${row.extra_option_by}${row.extra_option_at ? ` · ${String(row.extra_option_at).slice(0, 10)}` : ''}`
              : '—'}
            {memoLocked ? ` · ${t('xPricing.locked')}` : ''}
          </div>
        </div>
        {canDecide && !!decided && (
          <button type="button" className="sp-btn pad-savins" disabled={busy}
            onClick={() => onDecide(decided, instruction)}>
            {t('pricing.padSaveInstruction')}
          </button>
        )}
      </div>
      </>)}

      {/* ---- the Sales OP memo, written once ---------------------------------------------- */}
      {!memoLocked && !(c && c.cbd && !decided) && (
        <div className="pad-memo">
          <h5>{t('pricing.padMemo')}</h5>
          <p className="pad-note">{t('pricing.padMemoSub')}</p>
          <textarea rows={2} value={memo} disabled={!decided}
            placeholder={decided ? t('pricing.padMemoPh') : t('pricing.padMemoWait')}
            onChange={(e) => setMemo(e.target.value)} />
          <button type="button" className="sp-btn sp-btn-primary"
            disabled={busy || !decided || memo.trim().length < 4}
            onClick={() => onMemo(memo)}>
            {t('pricing.padMemoSave')}
          </button>
        </div>
      )}
      {/* ---- approve, reject, or leave it -------------------------------------------------- */}
      {/* THE GATE, AT THE FOOT OF WHAT IT IS ABOUT — Max, 2026-10-01: "thêm nút approve, reject
          hoặc cancel". These two decisions lived only in the list's Action column, so the person
          who had just read five screens of this quote had to close it and find the row again to
          act. They sit after the memo because that is the order the panel is read in: the figures,
          who carries the extra, the final terms, and only then yes or no.
          Drawn only for somebody who may actually decide at THIS stage — `canApprove` is the
          list's own rule, the manager at the manager step and the final approver at the CEO step —
          so the buttons never appear to a reader who would only be refused by the endpoint. */}
      {canGate && (
        <div className="pad-gate">
          <div className="pad-gate-txt">
            <b>{t('pricing.padGate')}</b>
            <span>{t('pricing.padGateSub', { status: row.approval_status })}</span>
          </div>
          {/* ONE BUTTON — Max, 2026-10-05: "ko cần nút leave it và send back nhé chỉ cần nút
              approve là dc". Leave it only ever closed the panel, which the X at the top already
              does and which pressing nothing also does; a control whose whole effect is "nothing
              happens" earns no place beside one that commits a decision.
              SENDING BACK HAS NOT GONE WITH IT, it moved inside: the dialog this opens decides
              SKU by SKU, and crossing every SKU goes to /reject carrying each one's own reason.
              On a single-SKU quote the dialog draws its own Agree / Reject switch, which is where
              that decision has always been taken. So the refusal is still one dialog away and now
              it arrives explained, instead of as one sentence covering a quote of eight. */}
          <div className="pad-gate-btns">
            <button type="button" className="sp-btn sp-btn-primary pad-gate-yes" disabled={busy}
              onClick={() => onGate('approve', seen)}>
              {t('pricing.padGateApprove')}
            </button>
          </div>
        </div>
      )}
    </div>
    </PadModal>
  );
}
